import React, { useEffect, useMemo, useState } from 'react';

import { useLocation, useNavigate, useParams } from 'react-router-dom';

import { CircularProgress } from '@mui/material';
import { CellContext, RowData } from '@tanstack/react-table';
import useCheckoutLinks from 'api/useCheckoutLinks';
import useOffers from 'api/useOffers';
import usePricing from 'api/usePricing';
import useUsers from 'api/useUsers';
import { Offer } from 'common/contracts';
import { EPermissionAction, EPermissionGroup } from 'common/permissions.types';
import { DATE_TIME_FORMAT } from 'constants/constants';
import { EFeatureFlag, OfferType, SortingOffersValue } from 'constants/enums';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import { usePaginationFromURL } from 'hooks/usePaginationFromURL';
import { useTableSorting } from 'hooks/useTableSorting';
import { getConvertedPrice } from 'utils/getConvertedPrice';
import { permissionsUtil } from 'utils/permissionsUtil';
import {
  appearanceUtils,
  statusMapping,
  statusUtils
} from 'utils/scheduleUtils';
import { OfferDesignTypes } from 'views/OffersUI/OffersUIForm/types';

import Chip from 'components/Chip/Chip';
import EditOfferDesignModal from 'components/EditOfferDesignModal/EditOfferDesignModal';

import ActionCell from '../../components/DataTableComponent/components/ActionCell';
import DataTable from '../../components/DataTableComponent/DataTable';
import DialogModal from '../../components/DialogModal/DialogModal';
import EmptyState from '../../components/EmptyState/EmptyState';
import FilterSearchCard from '../../components/Filter/FilterSearchCard';
import PortalHeader from '../../components/PortalHeader/PortalHeader';
import SortableHeader from '../../components/SortableHeader/SortableHeader';
import { EToasterStatus, showToast } from '../../utils/showToast';

const dayjsUTC = dayjs.extend(utc);

const PromotionsTable = () => {
  const { publisherId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { getPricings } = usePricing();
  const { generateOrGetCheckoutLink } = useCheckoutLinks(publisherId);
  const [generatingDeeplink, setGeneratingDeeplink] = useState(false);
  const { page, rows, sortValue, direction } = usePaginationFromURL('offers');
  const [currentPage, setCurrentPage] = useState(page);
  const [rowsPerPage, setRowsPerPage] = useState(rows);
  const [showLimitedEditModal, setShowLimitedEditModal] = useState(false);
  const { sorting, updateSorting } = useTableSorting(
    SortingOffersValue.CREATED_AT
  );

  const {
    getOffers: offers,
    deleteOffer,
    updateOffer,
    toggleOfferStatus
  } = useOffers({
    offerType: OfferType.SPECIAL_OFFER,
    customPagination: { currentPage, rowsPerPage },
    sorting
  });

  const totalCount = offers.data?.totalCount || 0;
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [selectedOffer, setSelectedOffer] = useState<string | null>(null);

  const [externalIdFilter, setExternalIdFilter] = useState('');
  const [nameFilter, setNameFilter] = useState('');

  const { fetchFeatureFlags } = useUsers({ publisherId });

  const hasScheduleOfferFeatureFlag =
    fetchFeatureFlags.data?.featureFlags?.[
      EFeatureFlag.DASHBOARD_SCHEDULED_OFFERS
    ];

  const canUpdateOfferStatus = useMemo(
    () =>
      permissionsUtil.isActionEnabled(
        null,
        EPermissionGroup.OFFERS,
        EPermissionAction.UPDATE_OFFER_STATUS,
        publisherId
      ),
    [publisherId]
  );

  useEffect(() => {
    const queryParams = new URLSearchParams();
    queryParams.set('page', String(currentPage));
    queryParams.set('rows', String(rowsPerPage));
    queryParams.set('sortValue', sorting.sortValue);
    queryParams.set('direction', sorting.direction);

    navigate(`${location.pathname}?${queryParams.toString()}`, {
      replace: true
    });
    offers.refetch();
    getPricings.refetch();
  }, [currentPage, rowsPerPage, sorting]);

  const handleDeleteOffer = async () => {
    if (!selectedOffer) return;
    await deleteOffer.mutate(selectedOffer, {
      onSuccess: () => {
        showToast({
          message: 'Promotion deleted successfully',
          status: EToasterStatus.SUCCESS
        });
        offers.refetch();
      },
      onError: (e: any) => {
        showToast({
          message: 'Failed to delete promotion',
          status: EToasterStatus.ERROR,
          description: e.response.data.message ?? ''
        });
      },
      onSettled: () => {
        setIsDeleteDialogOpen(false);
        setSelectedOffer(null);
      }
    });
  };

  const handleToggleStatus = (offerId: string, currentActive: boolean) => {
    toggleOfferStatus.mutate(
      { active: !currentActive, offerId },
      {
        onSuccess: () => {
          showToast({
            message: currentActive
              ? 'Promotion successfully deactivated'
              : 'Promotion successfully activated',
            status: EToasterStatus.SUCCESS
          });
          offers.refetch();
        },
        onError: (e: any) => {
          showToast({
            message: `Failed to ${currentActive ? 'deactivate' : 'activate'} promotion`,
            status: EToasterStatus.ERROR,
            description: e.response?.data?.message ?? ''
          });
        }
      }
    );
  };

  const handleGetDeeplink = async (offerId: string) => {
    setGeneratingDeeplink(true);
    await generateOrGetCheckoutLink.mutateAsync(
      { offerId },
      {
        onSuccess: (data: any) => {
          // Create a temporary input element
          const tempInput = document.createElement('input');
          tempInput.setAttribute('readonly', ''); // Prevent virtual keyboard on iOS
          tempInput.value = data.deeplink;
          tempInput.style.position = 'absolute';
          tempInput.style.left = '-9999px'; // Move off-screen
          document.body.appendChild(tempInput);

          try {
            if (navigator.userAgent.match(/ipad|iphone/i)) {
              // iOS specific handling
              tempInput.contentEditable = 'true';
              tempInput.readOnly = false;

              // Select the text
              const range = document.createRange();
              range.selectNodeContents(tempInput);
              const selection = window.getSelection();
              selection?.removeAllRanges();
              selection?.addRange(range);
              tempInput.setSelectionRange(0, 999999);
            } else {
              // All other browsers
              tempInput.select();
            }

            document.execCommand('copy');
            showToast({
              message:
                'Deeplink generated successfully and copied to clipboard',
              status: EToasterStatus.SUCCESS
            });
          } catch (err) {
            console.error('Copy failed:', err);
            showToast({
              message:
                'Deeplink generated successfully. Please copy it manually: ' +
                data.deeplink,
              status: EToasterStatus.ERROR
            });
          } finally {
            document.body.removeChild(tempInput);
          }
        },
        onError: (error: any) => {
          console.log('error', error);
          showToast({
            message: 'Something went wrong generating the deeplink',
            status: EToasterStatus.ERROR
          });
        },
        onSettled: () => {
          setGeneratingDeeplink(false);
        }
      }
    );
  };

  const handleEditOfferUI = (offerId: string): void => {
    navigate(`./form/${offerId}`);
  };

  const handleDuplicateOffer = (offerId: string): void => {
    navigate(`./dup/${offerId}`);
  };

  const handleDeletePopup = (offerId: string) => {
    if (!offerId) return;
    setSelectedOffer(offerId);
    setIsDeleteDialogOpen(true);
  };

  const COLUMNS: any = [
    {
      accessorKey: 'offerId',
      header: 'ID',
      cell: ({ getValue }: any) => getValue(),
      size: 100
    },
    {
      accessorKey: 'publisherOfferId',
      header: 'External ID',
      cell: ({ getValue }: any) => getValue(),
      size: 120
    },
    {
      accessorKey: 'offerUi.name',
      header: 'Offer Design',
      cell: ({ row }: any) => {
        if (offers.isLoading) return '';
        return <span className="truncate">{row.original.offerUi?.name}</span>;
      },
      size: 150
    },
    {
      accessorKey: 'name',
      header: 'Name',
      cell: ({ getValue }: any) => (
        <span className="truncate max-w-[200px] block">{getValue()}</span>
      ),
      size: 200
    },
    {
      accessorKey: 'createdAt',
      header: () => (
        <SortableHeader
          label="Created At"
          sortValue={SortingOffersValue.CREATED_AT}
          currentSortValue={sorting.sortValue}
          currentDirection={sorting.direction}
          onSort={updateSorting}
        />
      ),
      accessorFn: (row: any) => {
        return dayjsUTC.utc(row.createdAt).format(DATE_TIME_FORMAT);
      },
      cell: ({ row }: any) => {
        if (offers.isLoading) return '';
        return (
          <span className="whitespace-nowrap">
            {dayjsUTC.utc(row.original.createdAt).format(DATE_TIME_FORMAT)}
          </span>
        );
      },
      size: 180
    },
    {
      accessorKey: 'active',
      header: 'Status',
      cell: (cell: CellContext<RowData, unknown>) => {
        const isActive = cell.getValue() as boolean;
        const status = statusUtils.getOfferStatus(cell.row.original, isActive);

        return (
          <Chip variant="flat" status={statusMapping[status]}>
            {status}
          </Chip>
        );
      }
    },
    {
      accessorKey: 'nextAppearance',
      header: 'Next Appearance',
      cell: ({ row }: any) => {
        const schedule = (row.original as Offer)?.schedule;

        if (schedule?.permanent && schedule?.interval) {
          return (
            <span className="whitespace-nowrap">
              {appearanceUtils.getNextAppearanceByRefreshTime(
                schedule.interval
              )}
            </span>
          );
        }

        if (schedule?.permanent && !schedule?.interval) {
          return <span>-</span>;
        }

        const nextUpcomingTime = appearanceUtils.getNextUpcomingTime(
          schedule?.timeFrames
        );

        return (
          <span className="whitespace-nowrap">{nextUpcomingTime || '-'}</span>
        );
      },
      size: 160
    },
    {
      accessorKey: 'price',
      header: 'Price',
      cell: ({ row }: any) => {
        if (offers.isLoading || getPricings.isLoading) return '';
        return getConvertedPrice(row.original, getPricings.data.result);
      },
      size: 120
    },
    {
      accessorKey: 'actions',
      header: null,
      maxSize: 60,
      minSize: 60,
      size: 60,
      cell: ({ row }: any) => (
        <div className="max-w-[60px] w-[60px]">
          <ActionCell
            rowData={row.original}
            actions={[
              {
                label: 'Edit',
                handler: () => {
                  handleEditOfferUI(row.original.offerId);
                },
                disabled:
                  !permissionsUtil.canUserEdit() ||
                  !permissionsUtil.isActionEnabled(
                    null,
                    EPermissionGroup.OFFERS,
                    EPermissionAction.MANAGE,
                    publisherId
                  ),
                hidden: !permissionsUtil.isActionEnabled(
                  null,
                  EPermissionGroup.OFFERS,
                  EPermissionAction.MANAGE,
                  publisherId
                )
              },
              {
                label: 'Edit',
                handler: () => {
                  setSelectedOffer(row.original.offerId);
                  setShowLimitedEditModal(true);
                },
                disabled:
                  !permissionsUtil.canUserEdit() ||
                  !permissionsUtil.isActionEnabled(
                    null,
                    EPermissionGroup.OFFERS,
                    EPermissionAction.UPDATE_OFFER_STATUS_DESIGN,
                    publisherId
                  ),
                hidden:
                  !permissionsUtil.isActionEnabled(
                    null,
                    EPermissionGroup.OFFERS,
                    EPermissionAction.UPDATE_OFFER_STATUS_DESIGN,
                    publisherId
                  ) ||
                  permissionsUtil.isActionEnabled(
                    null,
                    EPermissionGroup.OFFERS,
                    EPermissionAction.MANAGE,
                    publisherId
                  )
              },
              {
                label: row.original.active ? 'Deactivate' : 'Activate',
                handler: () =>
                  handleToggleStatus(row.original.offerId, row.original.active),
                disabled: !canUpdateOfferStatus
              },
              {
                label: 'Duplicate',
                handler: () => handleDuplicateOffer(row.original.offerId),
                disabled:
                  !permissionsUtil.canUserEdit() ||
                  !permissionsUtil.isActionEnabled(
                    null,
                    EPermissionGroup.OFFERS,
                    EPermissionAction.MANAGE,
                    publisherId
                  )
              },
              {
                label: 'Delete',
                handler: () => handleDeletePopup(row.original.offerId),
                disabled:
                  !permissionsUtil.canUserEdit() ||
                  !permissionsUtil.isActionEnabled(
                    null,
                    EPermissionGroup.OFFERS,
                    EPermissionAction.MANAGE,
                    publisherId
                  )
              }
            ]}
          />
        </div>
      )
    }
  ];

  const promotions: Offer[] =
    (!offers.isLoading &&
      offers?.data?.offers &&
      offers?.data?.offers.map((offer: Offer) => ({
        _id: offer.publisherOfferId,
        id: offer.offerId,
        ...offer
      }))) ||
    [];

  const filteredPromotions = promotions.filter((promotion) => {
    const lowerExternalId = promotion.publisherOfferId.toLowerCase();
    const lowerName = promotion.name.toLowerCase();
    return (
      lowerExternalId.includes(externalIdFilter.toLowerCase()) &&
      lowerName.includes(nameFilter.toLowerCase())
    );
  });

  const totalPages = Math.ceil(totalCount / rowsPerPage);
  const BUTTON_TEXT = `${promotions.length > 0 ? 'Add' : 'Create'} New Promotion`;

  const renderContent = () => {
    if (offers.isLoading || offers.isFetching) {
      return (
        <CircularProgress className="absolute top-1/2 left-1/2 h-16 w-16" />
      );
    }

    if (promotions.length === 0) {
      return (
        <EmptyState
          title="Add your first Promotion"
          description="Promotions convert better - create your first one to boost sales and engagement"
          buttonText={BUTTON_TEXT}
          className="mx-auto mt-36"
          onClick={() => navigate('./form')}
        />
      );
    }

    return (
      <div className="border border-base-sidebarBorder rounded-lg mx-7 mb-9 overflow-hidden">
        <DataTable
          data={filteredPromotions}
          columns={COLUMNS}
          rowIdIdentifier="offerId"
          bodyDataTestId="dashPromotionsList"
          rowCount={totalCount}
          rowsPerPage={rowsPerPage}
          showPagination={totalPages > 1}
          manualPagination
          pageIndex={currentPage}
          pageCount={totalPages}
          onPageChange={setCurrentPage}
        />
      </div>
    );
  };

  return (
    <div className="overflow-y-scroll overflow-x-hidden">
      <PortalHeader
        title="Promotions"
        primaryButtonText={BUTTON_TEXT}
        onPrimaryButtonClick={() => navigate('./form')}
        primaryButtonState={{
          disabled: !permissionsUtil.canUserEdit(),
          hidden: !permissionsUtil.isActionEnabled(
            null,
            EPermissionGroup.OFFERS,
            EPermissionAction.MANAGE,
            publisherId
          )
        }}
        isCreateButton
      />
      {promotions.length > 0 && (
        <div className="flex px-7 gap-3 mb-5">
          <FilterSearchCard
            header="Promotions External ID"
            filterValue={externalIdFilter}
            setFilterValue={setExternalIdFilter}
            isLabelSearchBy
          />
          <FilterSearchCard
            header="Promotions Name"
            filterValue={nameFilter}
            setFilterValue={setNameFilter}
          />
        </div>
      )}
      {renderContent()}
      {showLimitedEditModal && (
        <EditOfferDesignModal
          offerId={selectedOffer || ''}
          offerDesignType={OfferDesignTypes.SpecialOffer}
          onClose={() => setShowLimitedEditModal(false)}
          isOpen={showLimitedEditModal}
          offerType={OfferType.SPECIAL_OFFER}
        />
      )}
      {isDeleteDialogOpen && (
        <DialogModal
          isOpen={isDeleteDialogOpen}
          title="Delete Promotion"
          description="Are you sure you want to delete this promotion?"
          modalActions={{
            onConfirm: { label: 'Delete', action: handleDeleteOffer },
            onClose: {
              label: 'Cancel',
              action: () => setIsDeleteDialogOpen(false),
              variant: 'link'
            }
          }}
        />
      )}
    </div>
  );
};

export default PromotionsTable;
